import React, {
  FC,
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Text, Snackbar } from '@amzn/storm-ui';
import { useBundle } from '@amzn/react-arb-tools';
import { getActionEventTarget } from '@amzn/aac-chrome-api';
import useChrome from '../../shared/chrome/hooks/useChrome';
import { ChromeKeys, ButtonState } from '../../shared/chrome/enums';
import {
  useGetActivateContextQuery,
  useSubmitActivationMutation,
  SubmitActivateBody,
} from '@services/activation/activation';
import {
  ManagerRegistrationPageWrapper,
  ManagerRegistrationPageInnerWrapper,
} from '../managerAccountRegistration/style';
import {
  DSP_ACTIVATION_ACTIVATE_PAGE_ID,
  SUBMIT_ERROR_KEY,
  GENERIC_ERROR_KEY,
  WARNING_BANNER_KEY,
  SNACKBAR_SUCCESS_KEY,
  SNACKBAR_ENTITY_IDS_KEY,
} from './constants';
import AdminSettings from './components/AdminSettings';
import ActivateBilling from './components/ActivateBilling';
import styled from 'styled-components';
import Banner from '@components/notifications/banner/Banner';
import { BannerTypes } from '@components/notifications/enums';
import { countryCurrencyTimezoneMap } from 'src/shared/country-data';

const SUBMIT_ACTION_ID = 'adspSubmitActivation';
const CANCEL_ACTION_ID = 'adspCancelActivation';

const StyledFieldSet = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

export interface ActivationActivateFormValues {
  timezone: string;
  marketplace: string;
  globalSellerOfRecord: string;
  amznAdSalesPartner: boolean;
  requirePoNumber: boolean;
  companyName: string;
  contactName: string;
  contactEmail: string;
  phoneNumber: string;
  website: string;
  businessRegistrationNumber: string;
  billingCountry: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  state: string;
  vatRegistrationNumber: string;
  vatAgreementAccepted: boolean;
  trTaxOffice: string;
  productType: string;
  paymentTerms: string;
}

function buildSubmitBody(
  formData: ActivationActivateFormValues,
  managerAccountId: string,
  principalObfuscatedId: string,
  amazonUsername: string,
  initialData: Record<string, unknown>,
): SubmitActivateBody {
  // Marketplace is sourced from initialData (set at KYC/request time) — not an
  // editable form field on this page. See legacy ActivationForm.tsx for reference.
  const initialMarketplace =
    (initialData?.marketplace as Record<string, unknown>) ?? {};
  const marketplaceCountryIsoCode =
    (initialMarketplace.countryIsoCode as string | undefined) || undefined;
  const multiMarketplace = Boolean(initialMarketplace.multiMarketplace);

  // Preserve any tax information already captured at KYC time.
  const initialBillingDetails =
    (initialData?.billingDetails as Record<string, unknown>) ?? {};
  const initialTaxInformation =
    (initialBillingDetails.taxInformation as Record<string, unknown>) ?? {};
  const taxInformation: Record<string, unknown> = { ...initialTaxInformation };
  if (formData.vatRegistrationNumber) {
    taxInformation.vatTaxInformation = {
      registrationNumber: formData.vatRegistrationNumber,
    };
  }
  if (formData.billingCountry === 'TR' && formData.trTaxOffice) {
    taxInformation.trTaxOffice = {
      registrationNumber: formData.trTaxOffice,
    };
  }

  return {
    principalObfuscatedId,
    managerAccountId,
    amazonUsername,
    activationRequest: {
      marketplace: {
        countryIsoCode: marketplaceCountryIsoCode,
        multiMarketplace,
      },
      companyName: formData.companyName,
      address: {
        line1: formData.addressLine1,
        line2: formData.addressLine2 || undefined,
        city: formData.city,
        state: formData.state || '',
        postalCode: formData.postalCode,
        countryIsoCode: formData.billingCountry,
      },
      contact: {
        name: formData.contactName,
        emails: formData.contactEmail,
        phoneNumber: formData.phoneNumber,
        website: formData.website || undefined,
        businessRegistrationNumber:
          formData.businessRegistrationNumber || undefined,
      },
      billingDetails: {
        mandatoryPurchaseOrderNumber: formData.requirePoNumber,
        taxInformation,
      },
      settings: {
        timeZone: formData.timezone,
        currency: countryCurrencyTimezoneMap.get(formData.globalSellerOfRecord)
          ?.currency,
        amznAdSalesPartner: formData.amznAdSalesPartner,
        globalSellerOfRecord: formData.globalSellerOfRecord,
        productType: formData.productType || undefined,
      },
    },
    activationTerms: {
      paymentTerms: formData.paymentTerms,
    },
  };
}

const DspActivationActivatePage: FC = () => {
  const [intl, isBundleLoading] = useBundle('pages.dspActivationActivate');
  const [searchParams] = useSearchParams();
  const managerAccountId = searchParams.get('managerAccountId') || '';
  const regionMatched = searchParams.get('regionMatched') === 'true';
  const {
    data: context,
    isLoading,
    isError,
    // Skip query until managerAccountId is available from URL params
  } = useGetActivateContextQuery(
    { managerAccountId, regionMatched },
    { skip: !managerAccountId },
  );
  const [submitActivation, { isLoading: isSubmitting }] =
    useSubmitActivationMutation();
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const contextRef = useRef(context);
  contextRef.current = context;

  const { updateButtons, buttons } = useChrome(
    ChromeKeys.DspActivationActivate,
  );

  // Disable the chrome submit button when the MA is already activated or a
  // submit is in flight (prevents rapid clicks firing concurrent activations).
  useEffect(() => {
    if (!buttons) return;
    const submitButton = buttons.get(SUBMIT_ACTION_ID);
    if (!submitButton) return;
    const shouldDisable = Boolean(context?.isActivated) || isSubmitting;
    const desiredState = shouldDisable
      ? ButtonState.Disabled
      : ButtonState.Enabled;
    if (submitButton.state === desiredState) return;
    const updated = new Map(buttons);
    updated.set(SUBMIT_ACTION_ID, {
      ...submitButton,
      state: desiredState,
    });
    updateButtons(updated);
  }, [context?.isActivated, isSubmitting, buttons, updateButtons]);

  const formValues = useMemo((): ActivationActivateFormValues => {
    const data = (context?.initialData ?? {}) as Record<string, unknown>;
    const address = data.address as Record<string, unknown> | undefined;
    const contact = data.contact as Record<string, unknown> | undefined;
    const settings = data.settings as Record<string, unknown> | undefined;
    const billing = data.billingDetails as Record<string, unknown> | undefined;
    const marketplace = data.marketplace as Record<string, unknown> | undefined;
    return {
      timezone: (settings?.timeZone as string) || '',
      marketplace: (marketplace?.countryIsoCode as string) || '',
      globalSellerOfRecord: (settings?.globalSellerOfRecord as string) || '',
      amznAdSalesPartner: Boolean(settings?.amznAdSalesPartner),
      requirePoNumber: Boolean(billing?.mandatoryPurchaseOrderNumber),
      companyName: (data.companyName as string) || '',
      contactName: (contact?.name as string) || '',
      contactEmail: (contact?.emails as string) || '',
      phoneNumber: (contact?.phoneNumber as string) || '',
      website: (contact?.website as string) || '',
      businessRegistrationNumber:
        (contact?.businessRegistrationNumber as string) || '',
      billingCountry: (address?.countryIsoCode as string) || '',
      addressLine1: (address?.line1 as string) || '',
      addressLine2: (address?.line2 as string) || '',
      postalCode: (address?.postalCode as string) || '',
      city: (address?.city as string) || '',
      state:
        (address?.stateIsoCode as string) || (address?.state as string) || '',
      vatRegistrationNumber: '',
      vatAgreementAccepted: false,
      trTaxOffice:
        ((
          (billing?.taxInformation as Record<string, unknown> | undefined)
            ?.trTaxOffice as Record<string, unknown> | undefined
        )?.registrationNumber as string | undefined) || '',
      productType: (settings?.productType as string) || '',
      paymentTerms: '',
    };
  }, [context?.initialData]);

  // Using `values` instead of `defaultValues` so the form reactively updates
  // when context loads. This ensures Storm UI Dropdowns render the correct
  // selected label without needing a separate reset() call.
  const methods = useForm<ActivationActivateFormValues>({
    values: formValues,
  });

  const onSubmit = useCallback(
    async (formData: ActivationActivateFormValues) => {
      if (isSubmitting) return;
      if (!contextRef.current?.principalObfuscatedId) return;
      setError(null);
      try {
        const body = buildSubmitBody(
          formData,
          managerAccountId,
          contextRef.current.principalObfuscatedId,
          contextRef.current.amazonUsername,
          (contextRef.current.initialData ?? {}) as Record<string, unknown>,
        );
        const result = await submitActivation({
          managerAccountId,
          body,
        }).unwrap();
        window.location.href = result.successUrl;
      } catch {
        setError(intl.getMessage(SUBMIT_ERROR_KEY));
      }
    },
    [isSubmitting, submitActivation, managerAccountId, intl],
  );

  useEffect(() => {
    const actionEventTarget = getActionEventTarget();
    const handleSubmit = () => formRef.current?.requestSubmit();
    const handleCancel = () => {
      window.location.href = '/';
    };

    actionEventTarget.addEventListener(SUBMIT_ACTION_ID, handleSubmit);
    actionEventTarget.addEventListener(CANCEL_ACTION_ID, handleCancel);

    return () => {
      actionEventTarget.removeEventListener(SUBMIT_ACTION_ID, handleSubmit);
      actionEventTarget.removeEventListener(CANCEL_ACTION_ID, handleCancel);
    };
  }, []);

  useEffect(() => {
    if (context?.redirectUrl) {
      window.location.href = context.redirectUrl;
    }
  }, [context?.redirectUrl]);

  if (isLoading || isBundleLoading) {
    return null;
  }

  if (isError || !managerAccountId || !context) {
    return <Text>{intl.getMessage(GENERIC_ERROR_KEY)}</Text>;
  }

  if (context.redirectUrl) {
    return null;
  }

  const entityIds = (context.shadowEntityIdList || []).join('\n');

  return (
    <ManagerRegistrationPageWrapper
      id={DSP_ACTIVATION_ACTIVATE_PAGE_ID}
      data-testid={DSP_ACTIVATION_ACTIVATE_PAGE_ID}
    >
      <ManagerRegistrationPageInnerWrapper>
        {/* Success snackbar when already activated */}
        {context.isActivated && (
          <Snackbar id="standard-success-snackbar" variant="success">
            <div>{intl.getMessage(SNACKBAR_SUCCESS_KEY)}</div>
            <div>
              {intl.getMessage(SNACKBAR_ENTITY_IDS_KEY)} <br /> {entityIds}
            </div>
          </Snackbar>
        )}
        <FormProvider {...methods}>
          <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)}>
            <StyledFieldSet disabled={context.isActivated}>
              {/* Warning banner for activated accounts */}
              {context.isActivated && (
                <Banner
                  id="activation-warning-banner"
                  props={{ variant: BannerTypes.Warning }}
                  message={intl.getMessage(WARNING_BANNER_KEY)}
                  duration={0}
                />
              )}
              {/* Error banner */}
              {error && (
                <Banner
                  id="activation-error-banner"
                  props={{ variant: BannerTypes.Error }}
                  message={error}
                  duration={0}
                />
              )}
              <AdminSettings intl={intl} context={context} />
              <ActivateBilling intl={intl} context={context} />
            </StyledFieldSet>
          </form>
        </FormProvider>
      </ManagerRegistrationPageInnerWrapper>
    </ManagerRegistrationPageWrapper>
  );
};

export default DspActivationActivatePage;
